import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  BookOpen,
  Award,
  TrendingUp,
  CheckCircle2,
  Download,
  FileText,
  Share2,
  Link2,
  Linkedin,
  Twitter,
  Facebook,
  Camera,
  Mail,
  MessageCircle,
  Lock,
} from "lucide-react";
import { StudentLayout } from "@/components/student/StudentLayout";
import { CourseCard, LockedCourseCard, ProgressBar } from "@/components/student/CourseCards";
import { TrainingSeries } from "@/components/student/TrainingSeries";
import { CertificationSection } from "@/components/student/Certification";
import { OnboardingModal } from "@/components/student/OnboardingModal";
import { apiError, studentApi, useStudentAuth } from "@/lib/student-auth";

const SUPPORT_EMAIL = "support@skillpreneuracademy.com";

const StatCard = ({ label, value, icon: Icon, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-4 sm:p-5"
  >
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</span>
      <Icon className="h-4 w-4 text-[#06B6D4]" />
    </div>
    <div className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tighter">{value}</div>
  </div>
);

const SectionTitle = ({ children, note }) => (
  <div className="mb-4">
    <h2 className="font-display text-base md:text-lg font-semibold tracking-tight">{children}</h2>
    {note && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{note}</p>}
  </div>
);

export default function StudentDashboard() {
  const { section = "dashboard" } = useParams();
  const nav = useNavigate();
  const { student, checking, refresh } = useStudentAuth();
  const [data, setData] = useState(null);
  const [training, setTraining] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if (!checking && student === false) nav("/student/login");
  }, [checking, student, nav]);

  useEffect(() => {
    if (!student) return;
    studentApi.get("/student/dashboard").then((r) => setData(r.data)).catch(() => {});
    studentApi.get("/training-series").then((r) => setTraining(r.data)).catch(() => {});
    studentApi.get("/student/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  }, [student]);

  if (checking || !student) {
    return (
      <div data-testid="student-loading" className="min-h-screen grid place-items-center bg-[#F8FAFC] dark:bg-[#020617] text-sm text-slate-500">
        Loading your dashboard…
      </div>
    );
  }

  const stats = data?.stats || {};
  const myCourses = data?.my_courses || [];
  const locked = data?.locked_courses || [];

  const download = async (url, filename) => {
    try {
      const res = await studentApi.get(url, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Download failed"));
    }
  };

  return (
    <StudentLayout active={section}>
      <OnboardingModal />
      {section === "dashboard" && (
        <div data-testid="student-dashboard-home" className="space-y-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#06B6D4] font-semibold">
              {student.package_name || "No package yet"}
            </p>
            <h2 className="mt-1.5 font-display text-2xl sm:text-3xl font-semibold tracking-tighter">
              Hi {student.name?.split(" ")[0]}, let's keep going.
            </h2>
            <div className="mt-3 max-w-md">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                <span>Overall progress</span>
                <span data-testid="overall-percent" className="font-semibold text-[#0F172A] dark:text-white">
                  {stats.overall_percent || 0}%
                </span>
              </div>
              <ProgressBar percent={stats.overall_percent} />
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard testid="stat-courses" label="Courses" value={stats.courses_unlocked || 0} icon={BookOpen} />
            <StatCard testid="stat-completed" label="Completed" value={stats.courses_completed || 0} icon={CheckCircle2} />
            <StatCard testid="stat-sessions" label="Sessions Done" value={`${stats.sessions_completed || 0}/${stats.sessions_total || 0}`} icon={TrendingUp} />
            <StatCard testid="stat-certificates" label="Certificates" value={stats.certificates || 0} icon={Award} />
          </div>

          <div>
            <SectionTitle note="Pick up exactly where you left off.">Continue learning</SectionTitle>
            {myCourses.length === 0 ? (
              <div data-testid="no-courses-yet" className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-8 text-center">
                <Lock className="h-5 w-5 mx-auto text-slate-400" />
                <p className="mt-3 text-sm text-slate-500">
                  You don't have any courses yet. Choose a package to unlock your learning.
                </p>
                <Link to="/#packages" data-testid="empty-view-packages" className="mt-4 inline-flex rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-5 py-2.5 text-xs font-semibold">
                  View packages
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {myCourses.slice(0, 3).map((c, i) => (
                  <CourseCard key={c.slug} course={c} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {section === "courses" && (
        <div data-testid="student-my-courses" className="space-y-8">
          <div>
            <SectionTitle note={`${myCourses.length} course${myCourses.length === 1 ? "" : "s"} unlocked in your ${student.package_name || "account"}.`}>
              My courses
            </SectionTitle>
            {myCourses.length === 0 ? (
              <p className="text-sm text-slate-500">No courses unlocked yet.</p>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {myCourses.map((c, i) => (
                  <CourseCard key={c.slug} course={c} index={i} />
                ))}
              </div>
            )}
          </div>

          {locked.length > 0 && (
            <div>
              <SectionTitle note="Available in a higher package whenever you're ready.">
                More courses
              </SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
                {locked.map((c) => (
                  <LockedCourseCard key={c.slug} course={c} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {section === "training" && (
        <div data-testid="student-training" className="space-y-5">
          <SectionTitle note="Structured series — watch each part in order, streamed right here.">
            Training
          </SectionTitle>
          <TrainingSeries series={training} />
        </div>
      )}

      {section === "certification" && (
        <CertificationSection download={download} CertificateShare={CertificateShare} />
      )}

      {section === "profile" && (
        <ProfileSection student={student} refresh={refresh} invoices={invoices} download={download} />
      )}

      {section === "help" && (
        <div data-testid="student-help" className="space-y-5 max-w-2xl">
          <SectionTitle note="We reply to every student query within 24 hours.">Help & Support</SectionTitle>
          <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5">
            <Mail className="h-4 w-4 text-[#2563EB]" />
            <h3 className="mt-2 text-sm font-semibold">Email support</h3>
            <p className="mt-1 text-xs text-slate-500">
              Write to us with your Student ID ({student.student_id}) and we'll get back to you quickly.
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=Support%20request%20-%20${student.student_id}`}
              data-testid="help-email-link"
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2.5 text-xs font-semibold"
            >
              <MessageCircle className="h-3.5 w-3.5" /> {SUPPORT_EMAIL}
            </a>
          </div>
          <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] divide-y divide-slate-100 dark:divide-white/5">
            {[
              ["How do I access my courses?", "Open My Courses in the sidebar — every course in your package is listed there with your progress."],
              ["My video won't play", "Videos stream from Google Drive. Refresh the page, and if it persists email support with the session name."],
              ["When do I get my certificate?", "The moment a course hits 100% completion, your certificate is generated in the Certification tab."],
              ["Can I upgrade my package?", "Yes — locked courses show the package that unlocks them. Reach out to support for an upgrade link."],
              ["How do I download my invoice?", "Profile Settings has a Download invoice button for every enrollment."],
            ].map(([q, a], i) => (
              <details key={i} data-testid={`faq-${i}`} className="p-4 group">
                <summary className="cursor-pointer text-sm font-medium list-none flex items-center justify-between">
                  {q}
                  <span className="text-slate-400 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-2 text-xs text-slate-500 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      )}
    </StudentLayout>
  );
}

function CertificateShare({ cert }) {
  const [open, setOpen] = useState(false);
  const url = `${window.location.origin}/certificate/${cert.cert_id}`;
  const text = `I just completed ${cert.course_title} from SkillPreneur Academy!`;

  const options = [
    { id: "linkedin", label: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` },
    { id: "twitter", label: "X / Twitter", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}` },
    { id: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
  ];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`certificate-share-${cert.cert_id}`}
        className="inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-2 text-[11px] hover:border-[#06B6D4]/60 transition-colors"
      >
        <Share2 className="h-3 w-3" /> Share
      </button>
      {open && (
        <div
          data-testid={`certificate-share-menu-${cert.cert_id}`}
          className="absolute right-0 bottom-full mb-2 w-44 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0F172A] shadow-xl p-1.5 z-20"
        >
          {options.map((o) => (
            <a
              key={o.id}
              href={o.href}
              target="_blank"
              rel="noreferrer"
              data-testid={`cert-share-${o.id}-${cert.cert_id}`}
              className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-[11px] hover:bg-slate-50 dark:hover:bg-white/5"
            >
              <o.icon className="h-3.5 w-3.5 text-[#2563EB]" /> {o.label}
            </a>
          ))}
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(url);
              toast.success("Certificate link copied");
              setOpen(false);
            }}
            data-testid={`cert-share-copy-${cert.cert_id}`}
            className="w-full flex items-center gap-2 rounded-lg px-2.5 py-2 text-[11px] hover:bg-slate-50 dark:hover:bg-white/5"
          >
            <Link2 className="h-3.5 w-3.5 text-[#2563EB]" /> Copy link
          </button>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/certificates/${cert.cert_id}/download`}
            data-testid={`cert-share-pdf-${cert.cert_id}`}
            className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-[11px] hover:bg-slate-50 dark:hover:bg-white/5"
          >
            <Download className="h-3.5 w-3.5 text-[#2563EB]" /> Download PDF
          </a>
        </div>
      )}
    </div>
  );
}

function ProfileSection({ student, refresh, invoices, download }) {
  const [form, setForm] = useState({
    name: student.name || "",
    phone: student.phone || "",
    bio: student.bio || "",
    avatar_url: student.avatar_url || "",
  });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    setUploading(true);
    try {
      const { data } = await studentApi.post("/student/avatar", body);
      setForm((f) => ({ ...f, avatar_url: data.avatar_url }));
      await refresh();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Could not upload photo"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const cls =
    "w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30";

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await studentApi.put("/student/profile", form);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Could not update profile"));
    } finally {
      setSaving(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    try {
      await studentApi.post("/student/change-password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(apiError(err?.response?.data?.detail, "Could not change password"));
    }
  };

  return (
    <div data-testid="student-profile" className="grid lg:grid-cols-[1.2fr_1fr] gap-6 max-w-5xl">
      <div className="space-y-6">
        <form
          onSubmit={save}
          data-testid="profile-form"
          className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5"
        >
          <h3 className="font-display text-base font-semibold tracking-tight mb-4">Profile details</h3>
          <div className="flex items-center gap-4 mb-4">
            <div className="relative h-16 w-16 shrink-0 rounded-full overflow-hidden border border-slate-200 dark:border-white/10 bg-slate-100 dark:bg-white/5 grid place-items-center">
              {form.avatar_url ? (
                <img
                  data-testid="profile-avatar-preview"
                  src={form.avatar_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${form.avatar_url}` : form.avatar_url}
                  alt="Profile"
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="font-display text-lg font-semibold text-slate-400">
                  {(student.name || "S").charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <label
                data-testid="profile-avatar-upload-label"
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-4 py-2 text-[11px] font-semibold cursor-pointer hover:border-[#06B6D4]/60 transition-colors"
              >
                <Camera className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload photo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  data-testid="profile-avatar-input"
                  onChange={uploadAvatar}
                  className="hidden"
                />
              </label>
              <p className="mt-1.5 text-[11px] text-slate-500">JPG, PNG or WebP · up to 5 MB</p>
            </div>
          </div>
          <div className="space-y-3">
            <input data-testid="profile-name" className={cls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
            <input data-testid="profile-email" className={`${cls} opacity-60`} value={student.email} readOnly />
            <input data-testid="profile-phone" className={cls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Mobile number" />
            <textarea data-testid="profile-bio" rows={3} maxLength={500} className={cls} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="About you (max 500 characters)" />
            <p className="text-[11px] text-slate-500 text-right">{(form.bio || "").length}/500</p>
          </div>
          <button
            type="submit"
            data-testid="profile-save"
            disabled={saving}
            className="mt-4 inline-flex rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-5 py-2.5 text-xs font-semibold disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </form>

        <form
          onSubmit={changePw}
          data-testid="change-password-form"
          className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5"
        >
          <h3 className="font-display text-base font-semibold tracking-tight mb-4">Change password</h3>
          <div className="space-y-3">
            <input data-testid="current-password" type="password" required className={cls} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder="Current password" />
            <input data-testid="new-password" type="password" required minLength={8} className={cls} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="New password (min 8 chars)" />
          </div>
          <button type="submit" data-testid="change-password-submit" className="mt-4 inline-flex rounded-full border border-slate-200 dark:border-white/10 px-5 py-2.5 text-xs font-semibold">
            Update password
          </button>
        </form>
      </div>

      <div className="space-y-6">
        <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5">
          <h3 className="font-display text-base font-semibold tracking-tight mb-4">Enrollment</h3>
          <dl className="text-xs space-y-2.5">
            {[
              ["Student ID", student.student_id],
              ["Package", student.package_name || "—"],
              ["Enrolled on", student.enrolled_at ? new Date(student.enrolled_at).toLocaleDateString() : "—"],
              ["Courses access", (student.courses || []).length],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <dt className="text-slate-500">{k}</dt>
                <dd className="font-medium text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5">
          <h3 className="font-display text-base font-semibold tracking-tight mb-4">Invoices & receipts</h3>
          {invoices.length === 0 ? (
            <p className="text-xs text-slate-500">No invoices yet.</p>
          ) : (
            <ul className="space-y-2.5">
              {invoices.map((inv) => (
                <li key={inv.id} className="flex items-center justify-between gap-3 text-xs">
                  <span className="min-w-0">
                    <span className="block font-medium truncate">{inv.package_name}</span>
                    <span className="block text-slate-400">{inv.invoice_no}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => download(`/student/invoices/${inv.id}/download`, `${inv.invoice_no}.pdf`)}
                    data-testid={`invoice-download-${inv.id}`}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5"
                  >
                    <Download className="h-3 w-3" /> PDF
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
